import React, { ReactNode, useMemo } from 'react';

interface CompanyBranding {
  color_primary?: string | null;
  color_primary_variet?: string | null;
  image?: string | null;
  image_dark?: string | null;
  image_cell?: string | null;
}

interface WhiteLabelProviderProps {
  company?: CompanyBranding | null;
  children: ReactNode;
}

const hexToRgba = (hex: string, alpha: number): string => {
  if (!hex) return `rgba(0, 158, 247, ${alpha})`;
  let sanitised = hex.replace('#', '');
  if (sanitised.length === 3) {
    sanitised = sanitised.split('').map(char => char + char).join('');
  }
  const r = parseInt(sanitised.substring(0, 2), 16) || 0;
  const g = parseInt(sanitised.substring(2, 4), 16) || 0;
  const b = parseInt(sanitised.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const WhiteLabelProvider: React.FC<WhiteLabelProviderProps> = ({ company, children }) => {
  const styleContent = useMemo(() => {
    if (!company || (!company.color_primary && !company.color_primary_variet)) {
      return null;
    }

    const primaryColor = company.color_primary || '#009ef7';
    const primaryActive = company.color_primary_variet || primaryColor;
    const primaryLight = hexToRgba(primaryColor, 0.12);

    return `
      .whitelabel-scope {
        --bs-primary: ${primaryColor} !important;
        --bs-primary-active: ${primaryActive} !important;
        --bs-primary-light: ${primaryLight} !important;
        --bs-primary-inverse: #ffffff !important;
      }
      
      .whitelabel-scope .text-primary,
      .whitelabel-scope .text-active-primary.active,
      .whitelabel-scope .text-hover-primary:hover {
        color: var(--bs-primary) !important;
      }
      .whitelabel-scope .bg-primary {
        background-color: var(--bs-primary) !important;
      }
      .whitelabel-scope .bg-light-primary {
        background-color: var(--bs-primary-light) !important;
      }
      .whitelabel-scope .btn-primary {
        background-color: var(--bs-primary) !important;
        border-color: var(--bs-primary) !important;
      }
      .whitelabel-scope .btn-primary:hover,
      .whitelabel-scope .btn-primary:active,
      .whitelabel-scope .btn-primary.active {
        background-color: var(--bs-primary-active) !important;
        border-color: var(--bs-primary-active) !important;
      }
      .whitelabel-scope .btn-active-primary:hover,
      .whitelabel-scope .btn-active-primary:active,
      .whitelabel-scope .btn-active-primary.active {
        background-color: var(--bs-primary) !important;
        border-color: var(--bs-primary) !important;
      }
      .whitelabel-scope .border-primary {
        border-color: var(--bs-primary) !important;
      }
      .whitelabel-scope .nav-line-tabs .nav-link.active,
      .whitelabel-scope .nav-line-tabs .nav-item.show .nav-link {
        border-bottom-color: var(--bs-primary) !important;
        color: var(--bs-primary) !important;
      }
      .whitelabel-scope .svg-icon-primary svg [fill]:not([fill="none"]) {
        fill: var(--bs-primary) !important;
      }
      .whitelabel-scope .form-check-input:checked {
        background-color: var(--bs-primary) !important;
        border-color: var(--bs-primary) !important;
      }
      .whitelabel-scope .bullet.bg-primary {
        background-color: var(--bs-primary) !important;
      }
      .whitelabel-scope .border-primary.border-dashed {
        border-color: var(--bs-primary) !important;
      }
      .whitelabel-scope .btn-light-primary {
        color: var(--bs-primary) !important;
        background-color: var(--bs-primary-light) !important;
      }
      .whitelabel-scope .btn-light-primary:hover,
      .whitelabel-scope .btn-light-primary:active {
        color: var(--bs-primary-inverse) !important;
        background-color: var(--bs-primary) !important;
      }
      .whitelabel-scope .badge-light-primary {
        color: var(--bs-primary) !important;
        background-color: var(--bs-primary-light) !important;
      }
      .whitelabel-scope .btn-active-color-primary:hover,
      .whitelabel-scope .btn-active-color-primary.active {
        color: var(--bs-primary) !important;
      }
      .whitelabel-scope .btn-active-light-primary:hover,
      .whitelabel-scope .btn-active-light-primary.active {
        color: var(--bs-primary) !important;
        background-color: var(--bs-primary-light) !important;
      }
    `;
  }, [company?.color_primary, company?.color_primary_variet]);

  if (!styleContent) {
    return <>{children}</>;
  }

  return (
    <>
      <style>{styleContent}</style>
      <div className="whitelabel-scope" style={{ display: 'contents' }}>
        {children}
      </div>
    </>
  );
};
